import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { marginApi } from '../../api/endpoints.ts';
import { ApiRequestError } from '../../api/client.ts';
import { useDebouncedValue } from '../../hooks/index.ts';
import { useSession } from '../../state/session.tsx';
import { usePlanFeature } from '../plan-gate.tsx';
import { Alert, Button, TextArea } from '../ui.tsx';
import { formatCents } from '@shared/calculations/pricing.ts';
import { getDateLocale } from '../../i18n/locale.ts';
import type { Quote, QuoteMarginView } from '@shared/types/index.ts';

/**
 * Margin-gated discounts (migration 0118) on the quote screen: each line's
 * expected margin after the discount being typed, the company's floor, and —
 * when the discount takes a line below it — the way through: a request for an
 * owner's or manager's approval, or, for them, applying it with a reason.
 *
 * Staff only. This panel lives in the editor and nothing in it is ever on
 * the customer's quote, PDF or page. Pro (`profit_insights`).
 */
export function formatMarginPercent(basisPoints: number | null): string {
  return basisPoints === null ? '—' : `${(basisPoints / 100).toFixed(1)}%`;
}

export default function QuoteMarginPanel({
  quote,
  serverQuote,
  currencyCode,
  readOnly,
  onApplied,
}: {
  /** The draft, carrying the discount being typed. */
  quote: Quote;
  /** The quote as last saved, whose version a request is made against. */
  serverQuote: Quote | null;
  currencyCode: string;
  readOnly: boolean;
  onApplied: (quote: Quote) => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { can } = useSession();
  const allowed = usePlanFeature('profit_insights') && can('quotes:read');
  // Debounced as primitives: a fresh object every render would never settle.
  const discountType = useDebouncedValue(quote.totals.discountType, 400);
  const discountValue = useDebouncedValue(quote.totals.discountValue, 400);
  const discount = { discountType, discountValue };
  const view = useQuery({
    queryKey: ['quote-margin', quote.id, serverQuote?.version ?? 0, discount.discountType, discount.discountValue],
    queryFn: () => marginApi.preview(quote.id, discount),
    enabled: allowed,
    staleTime: 15_000,
    retry: false,
  });
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState<{ tone: 'success' | 'error' | 'info'; text: string } | null>(null);
  const money = (cents: number) => formatCents(cents, currencyCode, getDateLocale());

  const request = useMutation({
    mutationFn: () => {
      if (!serverQuote || !quote.totals.discountType) throw new Error(t('quoteMargin.nothingToRequest'));
      return marginApi.requestOverride(quote.id, {
        discountType: quote.totals.discountType,
        discountValue: quote.totals.discountValue,
        reason: reason.trim(),
        expectedVersion: serverQuote.version,
      });
    },
    onSuccess: (result) => {
      setReason('');
      void queryClient.invalidateQueries({ queryKey: ['quote-margin', quote.id] });
      void queryClient.invalidateQueries({ queryKey: ['margin-overrides'] });
      if (result.applied && result.quote) {
        onApplied(result.quote);
        setMessage({ tone: 'success', text: t('quoteMargin.applied') });
      } else {
        setMessage({ tone: 'info', text: t('quoteMargin.requested') });
      }
    },
    onError: (caught) =>
      setMessage({
        tone: 'error',
        text:
          caught instanceof ApiRequestError || caught instanceof Error ? caught.message : t('quoteMargin.requestError'),
      }),
  });

  if (!allowed || !view.data) return null;
  const data: QuoteMarginView = view.data;
  if (!data.settings.available || !data.costed) return null;

  const floor = formatMarginPercent(data.settings.floorBasisPoints);
  const showCost = data.lines.some((line) => line.costCents !== null);
  const below = data.belowFloor;

  return (
    <section className="card mb-6" aria-labelledby="quote-margin-heading" data-testid="quote-margin-panel">
      <h2 id="quote-margin-heading" className="mb-1 text-lg font-semibold">
        {t('quoteMargin.heading')}
      </h2>
      <p className="mb-3 text-sm text-[color:var(--color-muted)]">
        {t('quoteMargin.staffOnly')} {t('quoteMargin.floorIs', { floor })}
      </p>

      <div className="overflow-x-auto">
        <table className="w-full min-w-max text-sm">
          <thead>
            <tr className="text-left text-[color:var(--color-muted)]">
              <th className="py-1 pr-3 font-medium">{t('quoteMargin.line')}</th>
              <th className="py-1 pr-3 text-right font-medium">{t('quoteMargin.price')}</th>
              <th className="py-1 pr-3 text-right font-medium">{t('quoteMargin.discount')}</th>
              {showCost ? <th className="py-1 pr-3 text-right font-medium">{t('quoteMargin.cost')}</th> : null}
              <th className="py-1 text-right font-medium">{t('quoteMargin.margin')}</th>
            </tr>
          </thead>
          <tbody>
            {data.lines.map((line) => (
              <tr key={line.key} className={line.belowFloor ? 'text-[color:var(--color-danger)]' : undefined}>
                <td className="py-1 pr-3">{line.label}</td>
                <td className="py-1 pr-3 text-right">{money(line.revenueCents)}</td>
                <td className="py-1 pr-3 text-right">
                  {line.discountCents > 0 ? `−${money(line.discountCents)}` : '—'}
                </td>
                {showCost ? (
                  <td className="py-1 pr-3 text-right">{line.costCents === null ? '—' : money(line.costCents)}</td>
                ) : null}
                <td className="py-1 text-right font-semibold">
                  {line.costCents === null && !showCost && line.marginBasisPoints === null
                    ? '—'
                    : formatMarginPercent(line.marginBasisPoints)}
                  {line.belowFloor ? ` · ${t('quoteMargin.belowFloor')}` : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {message ? (
        <div className="mt-3">
          <Alert tone={message.tone}>{message.text}</Alert>
        </div>
      ) : null}

      {below && data.settings.mode === 'block' ? (
        <div className="mt-3">
          <Alert tone="error" title={t('quoteMargin.blockedTitle')}>
            {t('quoteMargin.blockedBody', { floor })}
          </Alert>
        </div>
      ) : null}

      {below && data.settings.mode === 'override' && !readOnly ? (
        <div className="mt-3">
          <Alert tone="warning" title={t('quoteMargin.overrideTitle')}>
            <p>
              {data.canApprove
                ? t('quoteMargin.overrideBodyApprover', { floor })
                : t('quoteMargin.overrideBodyRequester', { floor })}
            </p>
            {data.pendingOverrideId && !data.canApprove ? <p className="mt-1">{t('quoteMargin.pending')}</p> : null}
            <TextArea
              label={t('quoteMargin.reason')}
              rows={2}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
            <div className="flex flex-wrap gap-2">
              <Button
                variant="primary"
                busy={request.isPending}
                disabled={!reason.trim() || !serverQuote}
                onClick={() => request.mutate()}
              >
                {data.canApprove ? t('quoteMargin.applyWithReason') : t('quoteMargin.requestApproval')}
              </Button>
              {data.canApprove ? (
                <Link to="/margin-overrides" className="btn">
                  {t('quoteMargin.viewRequests')}
                </Link>
              ) : null}
            </div>
          </Alert>
        </div>
      ) : null}
    </section>
  );
}
